import React, { useMemo } from 'react';
import { decodeText } from '../utils/base64';

interface PreviewProps {
  encodedData: string;
  mimeType: string;
  fileName: string;
}

const Preview: React.FC<PreviewProps> = ({ encodedData, mimeType, fileName }) => {
  const dataUrl = useMemo(() => {
    if (!encodedData || !mimeType) return null;
    // URL-safe characters must be converted back to standard Base64 for data URLs
    const standardBase64 = encodedData.replace(/-/g, '+').replace(/_/g, '/');
    return `data:${mimeType};base64,${standardBase64}`;
  }, [encodedData, mimeType]);

  const renderPreview = () => {
    if (!dataUrl) return null;

    if (mimeType.startsWith('image/')) {
      return (
        <img
          src={dataUrl}
          alt="Preview"
          className="max-w-full max-h-[400px] object-contain rounded-md mx-auto"
        />
      );
    }
    if (mimeType.startsWith('audio/')) {
      return <audio controls src={dataUrl} className="w-full" />;
    }
    if (mimeType.startsWith('video/')) {
      return (
        <video
          controls
          src={dataUrl}
          className="max-w-full max-h-[400px] rounded-md mx-auto"
        />
      );
    }
    if (mimeType === 'application/pdf') {
        return <iframe src={dataUrl} className="w-full h-[400px] rounded-md border border-gray-200" title={fileName}></iframe>;
    }
    if (mimeType === 'application/json') {
        try {
            const decoded = decodeText(encodedData, true); // Use true to handle both standard and url-safe
            const formattedJson = JSON.stringify(JSON.parse(decoded), null, 2);
            return <pre className="w-full h-full text-xs bg-gray-50 p-4 rounded-md overflow-auto max-h-[400px]"><code className="text-indigo-600">{formattedJson}</code></pre>;
        } catch {
             return <p className="text-red-500">Invalid JSON content.</p>;
        }
    }
     if (mimeType.startsWith('text/')) {
        try {
            const decoded = decodeText(encodedData, true); // Use true to handle both standard and url-safe
            return <pre className="w-full h-full text-sm bg-gray-50 p-4 rounded-md overflow-auto max-h-[400px]"><code className="text-gray-800">{decoded}</code></pre>;
        } catch {
             return <p className="text-red-500">Could not decode text.</p>;
        }
    }

    return (
      <a
        href={dataUrl}
        download={fileName}
        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-md transition-colors text-center"
      >
        Download {fileName}
      </a>
    );
  };

  if (!encodedData || !mimeType || mimeType === 'text/plain') return null;

  return (
    <div className="w-full mt-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Preview</h3>
      <div className="p-4 bg-white rounded-lg border border-gray-200 flex items-center justify-center min-h-[120px]">
        {renderPreview()}
      </div>
    </div>
  );
};

export default Preview;